// CalorieCalculator.jsx - Funnel: Form → Results → Motivation → Progress → Loading → Final CTA
import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import appStoreImg from "../assets/img/app-store.png";
import googlePlayImg from "../assets/img/google.store.png";
import { trackAppStoreClick, trackGooglePlayClick } from "../analytics/googleAnalytics";
import "./CalorieCalculator.css";
import { PRECIO_ANUAL } from "../config/precios";

const activityLevels = {
  sedentary: { label: "Sedentario", desc: "Trabajo de oficina, poco ejercicio", factor: 1.2 },
  light: { label: "Ligero", desc: "Ejercicio 1-2 días/semana", factor: 1.375 },
  moderate: { label: "Moderado", desc: "Ejercicio 3-5 días/semana", factor: 1.55 },
  active: { label: "Activo", desc: "Ejercicio 6-7 días/semana", factor: 1.725 },
  very_active: { label: "Muy activo", desc: "Atleta o trabajo físico intenso", factor: 1.9 },
};

const goalOptions = {
  lose: { label: "Perder peso", offset: -400 },
  maintain: { label: "Mantener peso", offset: 0 },
  gain_weight: { label: "Ganar peso", offset: 300 },
  gain_muscle: { label: "Ganar masa muscular", offset: 450 },
};

const motivationOptions = [
  { key: "health", emoji: "❤️", label: "Mejorar mi salud" },
  { key: "confidence", emoji: "💪", label: "Sentirme mejor conmigo" },
  { key: "energy", emoji: "⚡", label: "Tener más energía" },
  { key: "habit", emoji: "🎯", label: "Crear hábitos saludables" },
];

const loadingSteps = [
  "Analizando tus datos...",
  "Calculando tu objetivo de calorías...",
  "Prediciendo tu progreso...",
  "Preparando tu plan personalizado...",
];

export default function CalorieCalculator() {
  // Steps: "form" | "results" | "motivation" | "progress" | "loading" | "final"
  const [step, setStep] = useState("form");

  // Form data
  const [gender, setGender] = useState("female");
  const [age, setAge] = useState("");
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [activity, setActivity] = useState("moderate");
  const [goal, setGoal] = useState("");
  const [heightUnit, setHeightUnit] = useState("cm");
  const [weightUnit, setWeightUnit] = useState("kg");

  // Motivation
  const [motivation, setMotivation] = useState("");

  // Loading animation
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingPercent, setLoadingPercent] = useState(0);

  // Ebook email capture
  const [ebookEmail, setEbookEmail] = useState("");
  const [ebookSent, setEbookSent] = useState(false);
  const [ebookLoading, setEbookLoading] = useState(false);

  // Result
  const [result, setResult] = useState(null);

  const calculateResult = useCallback((overrideGoal) => {
    const w = parseFloat(weight);
    const h = parseFloat(height);
    const a = parseFloat(age);
    let bmr;
    if (gender === "male") {
      bmr = 10 * w + 6.25 * h - 5 * a + 5;
    } else {
      bmr = 10 * w + 6.25 * h - 5 * a - 161;
    }
    const tdee = bmr * activityLevels[activity].factor;
    const usedGoal = overrideGoal || goal;
    const goalOffset = (goalOptions[usedGoal] && goalOptions[usedGoal].offset) || 0;
    const target = Math.round(tdee + goalOffset);
    const protein = Math.round(w * (usedGoal === "gain_muscle" ? 2 : 1.8));
    const fat = Math.round((target * 0.25) / 9);
    const carbsCal = target - protein * 4 - fat * 9;
    const carbs = Math.round(carbsCal / 4);
    return { bmr: Math.round(bmr), tdee: Math.round(tdee), target, protein, fat, carbs };
  }, [weight, height, age, gender, activity, goal]);

  // Progress prediction helper
  const getProgressData = useCallback(() => {
    const w = parseFloat(weight);
    if (!w || !result) return [];
    const weeklyChange = goal === "lose" ? -0.75 : goal === "maintain" ? 0 : goal === "gain_weight" ? 0.55 : 0.5;
    return [
      { week: "Hoy", kg: w },
      { week: "4 sem", kg: Math.round((w + weeklyChange * 4) * 10) / 10 },
      { week: "8 sem", kg: Math.round((w + weeklyChange * 8) * 10) / 10 },
      { week: "12 sem", kg: Math.round((w + weeklyChange * 12) * 10) / 10 },
    ];
  }, [weight, goal, result]);

  // Loading animation effect
  useEffect(() => {
    if (step !== "loading") return;

    const totalDuration = 4000;
    const stepDuration = totalDuration / loadingSteps.length;

    const progressInterval = setInterval(() => {
      setLoadingPercent((prev) => {
        if (prev >= 85) return 85;
        return prev + 1;
      });
    }, totalDuration / 85);

    const stepTimers = loadingSteps.map((_, i) =>
      setTimeout(() => setLoadingStep(i), i * stepDuration)
    );

    const doneTimer = setTimeout(() => {
      setLoadingPercent(100);
      setTimeout(() => {
        // Recalculate with selected goal for the final plan
        setResult(calculateResult(goal));
        setStep("final");
      }, 350);
    }, totalDuration + 500);

    return () => {
      clearInterval(progressInterval);
      stepTimers.forEach(clearTimeout);
      clearTimeout(doneTimer);
    };
  }, [step, calculateResult, goal]);

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  // Form submit → results
  const handleFormSubmit = (e) => {
    e.preventDefault();
    const w = parseFloat(weight);
    const h = parseFloat(height);
    const a = parseFloat(age);
    if (!w || !h || !a) return;
    setResult(calculateResult("maintain")); // Base TDEE, no goal offset yet
    setStep("results");
  };

  // Reset
  const handleReset = () => {
    setStep("form");
    setResult(null);
    setGoal("");
    setMotivation("");
    setLoadingStep(0);
    setLoadingPercent(0);
  };

  // Ebook email submit
  const handleEbookSubmit = async (e) => {
    e.preventDefault();
    if (!ebookEmail || ebookLoading) return;
    setEbookLoading(true);
    try {
      await fetch("https://api.wayoai.app/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: ebookEmail, fuente: "calorie_calculator_ebook" }),
      });
      setEbookSent(true);
      // Open ebook in new tab
      window.open("/50recetassaludables.html", "_blank");
    } catch (err) {
      console.error("Ebook email error:", err);
    } finally {
      setEbookLoading(false);
    }
  };

  const progressData = getProgressData();

  // Target date (12 weeks from now) and target weight for final step
  const getTargetDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 84); // 12 weeks
    return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
  };
  const getTargetWeight = () => {
    if (!progressData.length) return parseFloat(weight);
    return progressData[3].kg;
  };

  return (
    <>
      {/* La barra superior ya no se pinta aquí: la monta `App.jsx` una sola vez
          para todo el sitio (components/CampanaBar.jsx). Este banner estaba
          copiado en cuatro páginas y ninguna de las tres copias de fuera de la
          portada se enteró de la campaña de septiembre. */}

      <Helmet>
        <html lang="es" />
        <title>Contador de Calorías Gratis - Calculadora Online | Wayo</title>
        <meta
          name="description"
          content="Calcula gratis cuántas calorías necesitas al día. Calculadora online con fórmula Mifflin-St Jeor: introduce tu peso, altura, edad y objetivo para obtener tus calorías y macros."
        />
        <meta
          name="keywords"
          content="contador de calorías gratis, calculadora calorías online, cuántas calorías necesito, calculadora TDEE, contador calorías online gratis"
        />
        <link rel="canonical" href="https://wayoai.app/contador-calorias" />

        {/* LÍNEA CLAVE: Forzamos a Google a indexar y a mostrar imágenes grandes */}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="googlebot" content="index, follow, max-image-preview:large" />

        {/* Open Graph / Twitter cards for better sharing */}
        <meta property="og:title" content="Contador de Calorías Gratis - Calculadora Online | Wayo" />
        <meta property="og:description" content="Calcula gratis cuántas calorías necesitas al día. Introduce tu peso, altura, edad y objetivo para obtener tus calorías y macros." />
        <meta property="og:image" content="https://wayoai.app/share/contador-calorias.jpg" />
        <meta property="og:image:alt" content="Calcula gratis cuantas calorías necesitas al día con nuestra calculadora Mifflin-St Jeor" />
        <meta property="og:url" content="https://wayoai.app/contador-calorias" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contador de Calorías Gratis - Calculadora Online | Wayo" />
        <meta name="twitter:description" content="Calcula gratis cuántas calorías necesitas al día. Introduce tu peso, altura, edad y objetivo para obtener tus calorías y macros." />
        <meta name="twitter:image" content="https://wayoai.app/share/contador-calorias.jpg" />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Calculadora de Calorías Wayo",
            "operatingSystem": "Web",
            "applicationCategory": "HealthApplication",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "EUR"
            },
            "description": "Calculadora nutricional gratuita que utiliza la fórmula Mifflin-St Jeor para calcular TDEE y macros personalizados."
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "¿Cuál es la fórmula más precisa para calcular calorías?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "En Wayo utilizamos la fórmula de Mifflin-St Jeor. Es la más recomendada por nutricionistas actuales porque se ajusta mejor al estilo de vida moderno y tiene un margen de error menor al 10%."
                }
              },
              {
                "@type": "Question",
                "name": "¿Qué es el TDEE y por qué es clave para perder peso?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "El TDEE es el gasto energético total diario. Incluye tu metabolismo basal, tu actividad física y la energía que gastas al digerir alimentos. Conocerlo es clave para crear un déficit moderado y perder grasa de forma sostenible."
                }
              },
              {
                "@type": "Question",
                "name": "¿Cuántas calorías debe comer un hombre de 80 kg?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Depende de su altura y nivel de actividad. Un hombre de 80 kg sedentario puede rondar las 2100 kcal, mientras que uno muy activo puede subir hacia 2800 kcal. Por eso conviene usar una calculadora personalizada."
                }
              },
              {
                "@type": "Question",
                "name": "¿Cómo calcular mis macros después de saber mis calorías?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Una guía práctica es: proteína entre 1.8 g y 2.2 g por kilo de peso, grasas entre el 20% y el 30% de las calorías totales y el resto en carbohidratos. Wayo hace este cálculo automáticamente."
                }
              },
              {
                "@type": "Question",
                "name": "¿Es fiable contar calorías para perder peso?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sí, es uno de los métodos más fiables para perder peso, pero registrar todo a mano suele ser tedioso. Por eso Wayo usa inteligencia artificial para estimar calorías y macros a partir de una foto."
                }
              }
            ]
          }
        `}</script>
      </Helmet>

      <Header />

      <main
        id="calculator-main"
        className="calc-page"
        itemScope
        itemType="https://schema.org/WebPage"
      >
        {/* Microdata for Schema.org */}
        <meta itemProp="name" content="Contador de Calorías Gratis - Calculadora Online | Wayo" />
        <meta itemProp="description" content="Calcula gratis cuántas calorías necesitas al día. Calculadora online con fórmula Mifflin-St Jeor: introduce tu peso, altura, edad y objetivo para obtener tus calorías y macros." />
        <meta itemProp="url" content="https://wayoai.app/contador-calorias" />
        <meta itemProp="image" content="https://wayoai.app/share/contador-calorias.jpg" />
        <div itemProp="author" itemScope itemType="https://schema.org/Person" style={{ display: 'none' }}>
          <meta itemProp="name" content="Wayo" />
        </div>
        <div itemProp="publisher" itemScope itemType="https://schema.org/Organization" style={{ display: 'none' }}>
          <meta itemProp="name" content="Wayo" />
        </div>

        <div className="calc-container">
          {/* =================== STEP: FORM =================== */}
          {step === "form" && (
            <>
              <div className="calc-header">
                <h1>Contador de Calorías Gratis</h1>
                <p>
                  Calcula cuántas calorías necesitas al día según tu cuerpo, actividad y objetivo.
                </p>
              </div>

              <div className="calc-form-wrapper">
                <form className="calc-form" onSubmit={handleFormSubmit}>
                  <section className="container">
                    <div className="row mt-0 mt-md-32">
                      <div className="col-12 col-md-6">
                        <div className="calculator-step" id="calculator-step-1">
                          <div className="line-dot"><div className="dot">1</div></div>
                          <h2 className="mb-0 mb-md-32">¿Cuál es tu género?</h2>
                          <div className="mt-16 icn-btn-wrapper" id="gender-icn-btn-wrapper">
                            <div className={`img-btn-container js-input-field ${gender === 'male' ? 'focused' : ''}`} tabIndex={1} onClick={() => setGender('male')}>
                              <div className="icon-svg">👨</div>
                              <div className="choice-btn yz-btn grey font-subtitle">Hombre</div>
                            </div>
                            <div className={`img-btn-container js-input-field ${gender === 'female' ? 'focused ml-32' : 'ml-32'}`} tabIndex={2} onClick={() => setGender('female')}>
                              <div className="icon-svg">👩</div>
                              <div className="choice-btn yz-btn grey font-subtitle">Mujer</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <div className="calculator-step" id="calculator-step-2">
                          <div className="line-dot"><div className="dot">2</div></div>
                          <h2 className="mb-0 mb-md-32">¿Cuántos años tienes?</h2>
                          <div className="mt-16 input-with-unit">
                                <input type="tel" className="input dual-number js-input-field" placeholder="21" maxLength={3} id="calculator-step-age" name="calculator-step-age" pattern="[0-9]*" value={age ?? ""} onChange={e=>setAge(e.target.value)} tabIndex={0} inputMode="tel" />
                            <span className="input-unit">años</span>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-6 mt-0 mt-md-32">
                        <div className="calculator-step" id="calculator-step-3">
                          <div className="line-dot"><div className="dot">3</div></div>
                          <h2 className="mb-0 mb-md-32">¿Cuánto mides?</h2>
                          <div className="mt-16">
                            <div className="unit-switch">
                              <button type="button" className={`unit-btn ${heightUnit==='cm' ? 'active' : ''}`} onClick={()=>setHeightUnit('cm')}>cm</button>
                              <button type="button" className={`unit-btn ${heightUnit==='ft' ? 'active' : ''}`} onClick={()=>setHeightUnit('ft')}>ft/in</button>
                            </div>

                            {heightUnit === 'cm' ? (
                              <div className="input-with-unit">
                                <input type="tel" id="calculator-step-height-cm" name="height[cm]" className="centered js-input-field input" placeholder="170" value={height ?? ""} maxLength={3} onChange={e=>setHeight(e.target.value)} tabIndex={0} inputMode="tel" />
                                <span className="input-unit">cm</span>
                              </div>
                            ) : (
                              <div className="ft-in-row">
                                <input type="tel" id="calculator-step-height-feet" placeholder="5" className="input small" />
                                <input type="tel" id="calculator-step-height-inch" placeholder="7" className="input small" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-6 mt-0 mt-md-32">
                        <div className="calculator-step" id="calculator-step-4">
                          <div className="line-dot"><div className="dot">4</div></div>
                          <h2 className="mb-0 mb-md-32">¿Cuánto pesas?</h2>
                          <div className="mt-16">
                            <div className="unit-switch">
                              <button type="button" className={`unit-btn ${weightUnit==='kg' ? 'active' : ''}`} onClick={()=>setWeightUnit('kg')}>kg</button>
                              <button type="button" className={`unit-btn ${weightUnit==='lb' ? 'active' : ''}`} onClick={()=>setWeightUnit('lb')}>lb</button>
                            </div>

                            <div className="input-with-unit">
                              <input type="tel" id="calculator-step-weight-input-kg" name="weight" className="d-inline-block centered js-input-field input form-control" placeholder="69" maxLength={5} value={weight ?? ""} onChange={e=>setWeight(e.target.value)} tabIndex={0} inputMode="tel" />
                              <span className="input-unit">{weightUnit}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 mt-0 mt-md-32">
                        <div className="calculator-step calculator-step-last" id="calculator-step-5">
                          <div className="line-dot"><div className="dot">5</div></div>
                          <h2 className="mb-0 mb-md-32">¿Cómo de activo eres a diario?</h2>
                          <div className="mt-16 daily-activities-wrapper">
                            <div className="icn-btn-wrapper">
                              {Object.entries(activityLevels).map(([key, val], idx) => (
                                <div key={key} className={`img-btn-container js-input-field ${activity === key ? 'focused' : ''}`} tabIndex={6+idx} onClick={() => setActivity(key)} data-value={val.factor}>
                                  <div className="icon-svg small">🏃</div>
                                  <div className="choice-btn yz-btn grey font-subtitle">{val.label}</div>
                                  <div className="daily-activity-item-description centered">{val.desc}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 form-error-wrapper centered hidden">
                        {/* error area if needed */}
                      </div>

                      <div className="col-12">
                        <button className="yz-btn pink js-submit-button calc-submit bold" style={{paddingLeft: '4rem', paddingRight: '4rem'}} id="calculator-daily-calories-submit">Calcula tus calorías diarias recomendadas</button>
                      </div>
                    </div>
                  </section>
                </form>
              </div>
            </>
          )}

          {/* =================== STEP: RESULTS + GOAL =================== */}
          {step === "results" && result && (
            <div className="calc-results-step">
              <button className="funnel-back-btn" onClick={() => setStep("form")}>← Volver</button>
              <div className="calc-results-card">
                <div className="results-hero">
                  <p className="results-hero-label">Tus calorías de mantenimiento</p>
                  <div className="results-hero-number">{result.tdee}</div>
                  <p className="results-hero-unit">kcal / día</p>
                  <p className="results-hero-range">Necesitas {result.tdee} kcal diarias para mantener tu peso actual</p>
                </div>

                <div className="results-goal-section">
                  <h3 className="results-goal-title">¿Cuál es tu objetivo?</h3>
                  <p className="results-goal-desc">Selecciona tu objetivo para personalizar tu plan</p>
                  <div className="goal-options">
                    {[
                      { key: "lose", emoji: "🔥", label: "Perder peso" },
                      { key: "maintain", emoji: "⚖️", label: "Mantener peso" },
                      { key: "gain_weight", emoji: "📈", label: "Ganar peso" },
                      { key: "gain_muscle", emoji: "💪", label: "Ganar músculo" },
                    ].map((g) => (
                      <button
                        key={g.key}
                        className={`goal-btn ${goal === g.key ? "active" : ""}`}
                        onClick={() => setGoal(g.key)}
                      >
                        <span className="goal-emoji">{g.emoji}</span>
                        <span className="goal-label">{g.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  className="funnel-continue-btn"
                  disabled={!goal}
                  onClick={() => setStep("motivation")}
                >
                  Continuar
                </button>
              </div>
            </div>
          )}

          {/* =================== STEP: MOTIVATION =================== */}
          {step === "motivation" && (
            <div className="funnel-step-card">
              <button className="funnel-back-btn" onClick={() => setStep("results")}>← Volver</button>
              <div className="funnel-step-icon">🎯</div>
              <h2>¿Qué te motiva a alcanzar tu objetivo?</h2>
              <p className="funnel-step-desc">Esto nos ayuda a personalizar tu experiencia</p>

              <div className="motivation-options">
                {motivationOptions.map((m) => (
                  <button
                    key={m.key}
                    className={`motivation-btn ${motivation === m.key ? "active" : ""}`}
                    onClick={() => setMotivation(m.key)}
                  >
                    <span className="motivation-emoji">{m.emoji}</span>
                    <span className="motivation-label">{m.label}</span>
                  </button>
                ))}
              </div>

              <button
                className="funnel-continue-btn"
                disabled={!motivation}
                onClick={() => setStep("progress")}
              >
                Continuar
              </button>
            </div>
          )}

          {/* =================== STEP: PROGRESS PREDICTION =================== */}
          {step === "progress" && result && (
            <div className="funnel-step-card">
              <button className="funnel-back-btn" onClick={() => setStep("motivation")}>← Volver</button>
              <div className="funnel-step-icon">📊</div>
              <h2>Tu progreso estimado</h2>
              <p className="funnel-step-desc">
                {goal === "lose"
                  ? "Así podría verse tu progreso si sigues el plan"
                  : goal === "maintain"
                  ? "Mantendrás tu peso estable con este plan"
                  : "Así podría verse tu progreso ganando peso de forma saludable"}
              </p>

              <div className="progress-timeline">
                {progressData.map((point, i) => (
                  <div key={i} className="progress-point">
                    <div className="progress-bar-visual">
                      <div
                        className="progress-bar-fill"
                        style={{
                          height: `${Math.max(20, Math.min(100, ((point.kg / progressData[0].kg) * 100)))}%`,
                        }}
                      />
                    </div>
                    <div className="progress-kg">{point.kg} kg</div>
                    <div className="progress-week">{point.week}</div>
                  </div>
                ))}
              </div>

              <div className="progress-summary">
                {goal === "lose" && (
                  <p>Podrías perder hasta <strong>{Math.abs(progressData[3].kg - progressData[0].kg).toFixed(1)} kg</strong> en 12 semanas</p>
                )}
                {goal === "gain_weight" && (
                  <p>Podrías ganar hasta <strong>{(progressData[3].kg - progressData[0].kg).toFixed(1)} kg</strong> en 12 semanas</p>
                )}
                {goal === "gain_muscle" && (
                  <p>Podrías ganar hasta <strong>{(progressData[3].kg - progressData[0].kg).toFixed(1)} kg</strong> de masa en 12 semanas</p>
                )}
                {goal === "maintain" && (
                  <p>Tu peso se mantendrá estable alrededor de <strong>{progressData[0].kg} kg</strong></p>
                )}
              </div>

              <button
                className="funnel-continue-btn"
                onClick={() => {
                  setLoadingStep(0);
                  setLoadingPercent(10);
                  setStep("loading");
                }}
              >
                Crear mi plan personalizado
              </button>
            </div>
          )}

          {/* =================== STEP: LOADING =================== */}
          {step === "loading" && (
            <div className="calc-loading-step">
              <div className="calc-loading-card">
                <div className="calc-loading-percent">{loadingPercent}%</div>
                <p className="calc-loading-title">Creando tu plan personalizado...</p>

                <div className="calc-progress-bar">
                  <div className="calc-progress-fill" style={{ width: `${loadingPercent}%` }} />
                </div>

                <div className="calc-loading-steps">
                  {loadingSteps.map((text, i) => (
                    <div key={i} className={`calc-loading-step-item ${i <= loadingStep ? "done" : ""} ${i === loadingStep ? "current" : ""}`}>
                      <span className="calc-step-check">
                        {i < loadingStep ? "\u2713" : i === loadingStep ? "..." : ""}
                      </span>
                      <span>{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* =================== STEP: FINAL CTA =================== */}
          {step === "final" && result && (
            <div className="final-step">
              <button className="funnel-back-btn" onClick={() => setStep("progress")}>← Volver</button>
              <div className="final-card">
                <h2>Tu Plan para llegar a {getTargetWeight()} kg</h2>

                {/* Prediction headline */}
                <div className="final-prediction">
                  <p className="final-prediction-label">Con este plan, estarás en</p>
                  <div className="final-prediction-weight">{getTargetWeight()} kg</div>
                  <p className="final-prediction-date">para el {getTargetDate()}</p>
                </div>

                {/* Calorie target visible */}
                <div className="final-calories-visible">
                  <p className="final-cal-label-top">Tu objetivo de calorías diarias:</p>
                  <span className="final-cal-number">{result.target}</span>
                  <span className="final-cal-label">kcal / día</span>
                </div>

                {/* Blurred/locked macros */}
                <div className="final-locked-section">
                  <div className="final-locked-blur">
                    <div className="final-summary">
                      <div className="final-summary-item">
                        <span className="final-summary-label">Proteína</span>
                        <span className="final-summary-value">{result.protein}g</span>
                      </div>
                      <div className="final-summary-item">
                        <span className="final-summary-label">Carbohidratos</span>
                        <span className="final-summary-value">{result.carbs}g</span>
                      </div>
                      <div className="final-summary-item">
                        <span className="final-summary-label">Grasas</span>
                        <span className="final-summary-value">{result.fat}g</span>
                      </div>
                      <div className="final-summary-item">
                        <span className="final-summary-label">BMR</span>
                        <span className="final-summary-value">{result.bmr}</span>
                      </div>
                    </div>
                  </div>
                  <div className="final-locked-overlay">
                    <span className="final-lock-icon">🔒</span>
                    <p>Inicia tu plan para descubrirlo</p>
                  </div>
                </div>

                {/* Price anchoring */}
                <div className="final-pricing">
                  <span className="final-pricing-badge">Oferta especial</span>
                  <div className="final-pricing-prices">
                    <span className="final-pricing-old">199&#8364;</span>
                    <span className="final-pricing-new">{PRECIO_ANUAL}<span className="final-pricing-period">/año</span></span>
                  </div>
                  <p className="final-pricing-trial">Pruébala gratis &middot; Sin tarjeta de crédito</p>
                </div>

                {/* Main CTA - Store cards */}
                <div className="final-cta">
                  <h3>Empieza tu plan gratis</h3>
                  <p>Escanea comidas con IA, registra calorías y macros, y alcanza tu objetivo.</p>

                  <div className="final-store-cards">
                    <a
                      className="final-store-card"
                      href="https://apps.apple.com/app/id6756500478"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackAppStoreClick("calorie_calculator_primary")}
                    >
                      <img src={appStoreImg} alt="Descargar en App Store" loading="lazy" />
                      <div className="final-store-card-info">
                        <span className="final-store-card-label">Empieza ahora en iPhone</span>
                        <span className="final-store-card-name">Calcula tus macros con una foto</span>
                      </div>
                    </a>
                    <a
                      className="final-store-card"
                      href="https://play.google.com/store/apps/details?id=com.facundosolis.wayo"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackGooglePlayClick("calorie_calculator_primary")}
                    >
                      <img src={googlePlayImg} alt="Descargar en Google Play" loading="lazy" />
                      <div className="final-store-card-info">
                        <span className="final-store-card-label">Empieza ahora en Android</span>
                        <span className="final-store-card-name">Escanea tu plato y obtén calorías</span>
                      </div>
                    </a>
                  </div>
                </div>

                {/* Ebook lead magnet */}
                <div className="final-ebook">
                  <div className="final-ebook-icon">📖</div>
                  <h4 className="final-ebook-title">+50 recetas saludables gratis</h4>
                  <p className="final-ebook-desc">Recibe nuestro ebook con más de 50 recetas mediterráneas saludables directamente en tu email.</p>
                  {ebookSent ? (
                    <div className="final-ebook-success">
                      <span>&#10003;</span> Enviado. Revisa tu email y disfruta las recetas.
                    </div>
                  ) : (
                    <form className="final-ebook-form" onSubmit={handleEbookSubmit}>
                      <input
                        type="email"
                        placeholder="tu@email.com"
                        value={ebookEmail}
                        onChange={(e) => setEbookEmail(e.target.value)}
                        required
                        className="final-ebook-input"
                      />
                      <button type="submit" className="final-ebook-btn" disabled={ebookLoading}>
                        {ebookLoading ? "Enviando..." : "Quiero el ebook gratis"}
                      </button>
                    </form>
                  )}
                </div>

                {/* Social proof */}
                <div className="final-social-proof">
                  <div className="final-proof-stats">
                    <div className="final-proof-stat">
                      <span className="final-proof-number">500+</span>
                      <span className="final-proof-text">usuarios activos</span>
                    </div>
                    <div className="final-proof-divider" />
                    <div className="final-proof-stat">
                      <span className="final-proof-number">&#9733;&#9733;&#9733;&#9733;&#9733;</span>
                      <span className="final-proof-text">valoración media</span>
                    </div>
                  </div>
                </div>

                {/* Testimonials */}
                <div className="final-testimonials">
                  <div className="final-testimonial">
                    <div className="final-testimonial-stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
                    <p className="final-testimonial-text">"Llevaba meses sin saber cuántas calorías comer. Con Wayo lo tengo claro y me resulta muy fácil cuidar mi alimentación."</p>
                    <p className="final-testimonial-author">Laura, 28 años</p>
                  </div>
                  <div className="final-testimonial">
                    <div className="final-testimonial-stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
                    <p className="final-testimonial-text">"Lo mejor es escanear la comida con la cámara. En 3 segundos tienes todas las calorías y macros. Muy fácil."</p>
                    <p className="final-testimonial-author">Carlos, 34 años</p>
                  </div>
                  <div className="final-testimonial">
                    <div className="final-testimonial-stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
                    <p className="final-testimonial-text">"Probé varias apps pero esta es la más simple y completa. Las recetas son un bonus increíble."</p>
                    <p className="final-testimonial-author">María, 42 años</p>
                  </div>
                </div>

                {/* Second CTA (repeat) */}
                <div className="final-cta final-cta-bottom">
                  <h3>Empieza tu plan gratis</h3>
                  <div className="final-store-cards">
                    <a
                      className="final-store-card"
                      href="https://apps.apple.com/app/id6756500478"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackAppStoreClick("calorie_calculator_secondary")}
                    >
                      <img src={appStoreImg} alt="Descargar en App Store" loading="lazy" />
                      <div className="final-store-card-info">
                        <span className="final-store-card-label">Empieza ahora en iPhone</span>
                        <span className="final-store-card-name">Calcula tus macros con una foto</span>
                      </div>
                    </a>
                    <a
                      className="final-store-card"
                      href="https://play.google.com/store/apps/details?id=com.facundosolis.wayo"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackGooglePlayClick("calorie_calculator_secondary")}
                    >
                      <img src={googlePlayImg} alt="Descargar en Google Play" loading="lazy" />
                      <div className="final-store-card-info">
                        <span className="final-store-card-label">Empieza ahora en Android</span>
                        <span className="final-store-card-name">Escanea tu plato y obtén calorías</span>
                      </div>
                    </a>
                  </div>
                </div>

                <button className="calc-reset-btn" onClick={handleReset}>
                  Volver a calcular
                </button>

                <div className="results-footer">
                  <small>&copy; 2026 Wayo &middot; <a href="/contact">Contacto</a> &middot; <a href="/cookies">Cookies</a> &middot; <a href="/privacy">Privacidad</a></small>
                </div>
              </div>
            </div>
          )}

          {/* SEO content - always visible */}
          <div className="calc-seo">
            <h2>¿Cómo funciona este contador de calorías?</h2>
            <p>
              Nuestro contador de calorías gratis utiliza la <strong>fórmula Mifflin-St Jeor</strong>,
              considerada la más precisa por los profesionales de la nutrición. Calcula tu metabolismo
              basal (BMR) y lo multiplica por tu nivel de actividad física para obtener tu gasto
              calórico total diario (TDEE).
            </p>

            <h3>¿Qué es el TDEE?</h3>
            <p>
              El TDEE (Total Daily Energy Expenditure) es el número total de calorías que tu cuerpo
              quema al día. Incluye tu metabolismo basal, la actividad física y la digestión de
              alimentos. Conocer tu TDEE es fundamental para saber cuánto comer según tu objetivo.
            </p>

            <h3>¿Cómo usar los resultados?</h3>
            <ul>
              <li><strong>Para perder peso:</strong> Come las calorías indicadas (déficit de ~400 kcal). Perderás 0.5-1 kg por semana.</li>
              <li><strong>Para mantener peso:</strong> Come tu TDEE. Tu peso se mantendrá estable.</li>
              <li><strong>Para ganar músculo:</strong> Come las calorías indicadas (superávit de ~300 kcal) y entrena fuerza.</li>
            </ul>

            <h3>¿Es preciso este cálculo?</h3>
            <p>
              La fórmula tiene un margen de error del 10%. Es un excelente punto de partida, pero
              tu cuerpo es único. Ajusta según resultados reales: si no pierdes peso en 2 semanas,
              reduce 200 kcal. Si pierdes muy rápido, sube 200 kcal.
            </p>
          </div>

          <section className="calc-seo calc-faq" aria-labelledby="calc-faq-title">
            <h2 id="calc-faq-title">FAQ: Preguntas Frecuentes sobre el Gasto Calórico</h2>

            <details className="calc-faq-item" open>
              <summary>1. ¿Cuál es la fórmula más precisa para calcular calorías?</summary>
              <div className="calc-faq-answer">
                <p>
                  En Wayo utilizamos la <strong>fórmula de Mifflin-St Jeor</strong>. Es la más recomendada
                  por nutricionistas actuales, por delante de la clásica Harris-Benedict, porque se ajusta
                  mejor al estilo de vida moderno y tiene un margen de error menor al 10%.
                </p>
              </div>
            </details>

            <details className="calc-faq-item">
              <summary>2. ¿Qué es el TDEE y por qué es clave para perder peso?</summary>
              <div className="calc-faq-answer">
                <p>
                  El <strong>TDEE (Total Daily Energy Expenditure)</strong> es el número total de calorías
                  que quemas al día. Si quieres adelgazar de forma saludable, nuestra calculadora recomienda
                  un déficit moderado, unas 400 kcal menos que tu TDEE, para ayudarte a perder grasa sin
                  pasar hambre ni perder masa muscular.
                </p>
              </div>
            </details>

            <details className="calc-faq-item">
              <summary>3. ¿Cuántas calorías debe comer un hombre de 80 kg?</summary>
              <div className="calc-faq-answer">
                <p>
                  Depende totalmente de la altura y la actividad física. Por ejemplo, un hombre de 80 kg
                  sedentario puede necesitar unas <strong>2.100 kcal</strong>, mientras que si es muy activo
                  podría subir a <strong>2.800 kcal</strong>. Por eso es mejor usar una calculadora personalizada.
                </p>
              </div>
            </details>

            <details className="calc-faq-item">
              <summary>4. ¿Cómo calcular mis macros después de saber mis calorías?</summary>
              <div className="calc-faq-answer">
                <p>
                  Una vez que tienes tu objetivo calórico, lo ideal es repartirlos así:
                </p>
                <ul>
                  <li><strong>Proteína:</strong> 1.8 g a 2.2 g por kilo de peso, vital para el músculo.</li>
                  <li><strong>Grasas:</strong> 20-30% de tus calorías totales.</li>
                  <li><strong>Carbohidratos:</strong> El resto de tus calorías para darte energía.</li>
                </ul>
                <p>
                  La app de Wayo hace este cálculo automáticamente por ti con solo una foto.
                </p>
              </div>
            </details>

            <details className="calc-faq-item">
              <summary>5. ¿Es fiable contar calorías para perder peso?</summary>
              <div className="calc-faq-answer">
                <p>
                  Es la forma más científica, pero registrar todo a mano es tedioso y ahí es donde la mayoría
                  falla. Por eso Wayo utiliza <strong>inteligencia artificial</strong> para que no tengas que
                  buscar en bases de datos: sacas una foto a tu plato y nosotros hacemos el trabajo pesado.
                </p>
              </div>
            </details>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}
